import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Trash2, ArrowUp, ArrowDown, Heading as HeadingIcon, Pilcrow, Quote, Image as ImageIcon, Images } from 'lucide-react'
import type { ContentItem } from '../../api/contentApi'

/* eslint-disable @typescript-eslint/no-explicit-any */
type Payload = Record<string, any>
type Block = Record<string, any>

interface EditorProps {
  item: ContentItem
  onSave: (payload: Payload) => void
  saving?: boolean
}

const BLOCK_TYPES = [
  { value: 'paragraph', label: 'Paragraph', icon: Pilcrow },
  { value: 'heading', label: 'Heading', icon: HeadingIcon },
  { value: 'pullquote', label: 'Pull-quote', icon: Quote },
  { value: 'image', label: 'Image', icon: ImageIcon },
  { value: 'duo', label: 'Two images', icon: Images },
]

function newBlock(type: string): Block {
  switch (type) {
    case 'heading': return { type, text: '' }
    case 'pullquote': return { type, text: '', cite: '' }
    case 'image': return { type, url: '', alt: '', caption: '', portrait: false }
    case 'duo': return { type, images: [{ url: '', alt: '' }, { url: '', alt: '' }], caption: '' }
    default: return { type: 'paragraph', text: '', dropCap: false }
  }
}

function setImg(images: any, k: number, field: string, v: string): Block[] {
  const imgs = Array.isArray(images) ? images.map((x) => ({ ...x })) : [{ url: '', alt: '' }, { url: '', alt: '' }]
  imgs[k] = { ...imgs[k], [field]: v }
  return imgs
}

export function EaselEditorialEditor({ item, onSave, saving }: EditorProps) {
  const [p, setP] = useState<Payload>((item.payload as Payload) || {})
  useEffect(() => { setP((item.payload as Payload) || {}) }, [item.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: string, v: any) => setP((prev) => ({ ...prev, [k]: v }))
  const blocks: Block[] = Array.isArray(p.editorial_blocks) ? p.editorial_blocks : []
  const setBlocks = (next: Block[]) => set('editorial_blocks', next)
  const updateBlock = (i: number, patch: Block) => setBlocks(blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)))
  const changeType = (i: number, type: string) => setBlocks(blocks.map((b, idx) => (idx === i ? newBlock(type) : b)))
  const move = (i: number, dir: number) => {
    const j = i + dir
    if (j < 0 || j >= blocks.length) return
    const next = blocks.slice()
    const tmp = next[i]; next[i] = next[j]; next[j] = tmp
    setBlocks(next)
  }
  const addBlock = (type: string) => setBlocks([...blocks, newBlock(type)])
  const removeBlock = (i: number) => setBlocks(blocks.filter((_, idx) => idx !== i))

  return (
    <div className="space-y-8">
      {/* Artist's perspective — the essay framing + voice */}
      <section className="space-y-5">
        <SectionTitle title="Artist’s perspective" subtitle="The framing and voice that sit above the essay." />
        <Field label="Perspective title" hint="The section heading, e.g. “The Language of Embrace.”">
          <Input value={String(p.perspective_title || '')} onChange={(e) => set('perspective_title', e.target.value)} placeholder="The Language of Embrace" />
        </Field>
        <Field label="Short artist quote" hint="A one-liner shown near the top of the page.">
          <Input value={String(p.artist_quote || '')} onChange={(e) => set('artist_quote', e.target.value)} />
        </Field>
        <Field label="Artist statement" hint="Rendered as a pull-quote, in the artist’s first-person voice.">
          <Textarea rows={3} value={String(p.artist_statement || '')} onChange={(e) => set('artist_statement', e.target.value)} />
        </Field>
        <Field label="Long description" hint="Plain long-form prose — used only when the essay below has no blocks.">
          <Textarea rows={5} value={String(p.long_description || '')} onChange={(e) => set('long_description', e.target.value)} />
        </Field>
      </section>

      <Separator />

      {/* The essay — ordered, image-integrated blocks */}
      <section className="space-y-4">
        <SectionTitle
          title="Editorial essay"
          subtitle="A designed, image-integrated story. Blocks render top-to-bottom on the artwork page; drag order with the arrows."
        />

        <div className="space-y-4">
          {blocks.map((b, i) => (
            <BlockCard
              key={i}
              block={b}
              index={i}
              total={blocks.length}
              onType={(t) => changeType(i, t)}
              onUpdate={(patch) => updateBlock(i, patch)}
              onMove={(d) => move(i, d)}
              onRemove={() => removeBlock(i)}
            />
          ))}
          {blocks.length === 0 && (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              No essay blocks yet. Add a paragraph or an image to begin the story.
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="mr-1 text-sm text-muted-foreground">Add block:</span>
          {BLOCK_TYPES.map((t) => (
            <Button key={t.value} variant="outline" size="sm" onClick={() => addBlock(t.value)}>
              <t.icon className="mr-1.5 h-4 w-4" />
              {t.label}
            </Button>
          ))}
        </div>
      </section>

      <div className="flex justify-end pt-2">
        <Button onClick={() => onSave(p)} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
      </div>
    </div>
  )
}

interface BlockCardProps {
  block: Block
  index: number
  total: number
  onType: (t: string) => void
  onUpdate: (patch: Block) => void
  onMove: (dir: number) => void
  onRemove: () => void
}

function BlockCard({ block: b, index, total, onType, onUpdate, onMove, onRemove }: BlockCardProps) {
  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <span className="w-5 text-xs font-medium text-muted-foreground">{index + 1}</span>
        <Select value={String(b.type || 'paragraph')} onValueChange={onType}>
          <SelectTrigger className="h-8 w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {BLOCK_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={index === 0} onClick={() => onMove(-1)} aria-label="Move up">
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={index === total - 1} onClick={() => onMove(1)} aria-label="Move down">
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={onRemove} aria-label="Remove block">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-3 p-3">
        {b.type === 'heading' && (
          <Field label="Heading text">
            <Input value={String(b.text || '')} onChange={(e) => onUpdate({ text: e.target.value })} placeholder="The florals are the language" />
          </Field>
        )}

        {b.type === 'paragraph' && (
          <>
            <Field label="Text" hint="Light inline HTML allowed: <a>, <em>, <strong>.">
              <Textarea rows={4} value={String(b.text || '')} onChange={(e) => onUpdate({ text: e.target.value })} />
            </Field>
            <ToggleRow label="Drop cap (large decorative first letter)" checked={!!b.dropCap} onChange={(v) => onUpdate({ dropCap: v })} />
          </>
        )}

        {b.type === 'pullquote' && (
          <>
            <Field label="Quote">
              <Textarea rows={3} value={String(b.text || '')} onChange={(e) => onUpdate({ text: e.target.value })} />
            </Field>
            <Field label="Attribution (optional)">
              <Input value={String(b.cite || '')} onChange={(e) => onUpdate({ cite: e.target.value })} placeholder="— Kaethe Butcher" />
            </Field>
          </>
        )}

        {b.type === 'image' && (
          <>
            <ImageField url={b.url} alt={b.alt} onUrl={(v) => onUpdate({ url: v })} onAlt={(v) => onUpdate({ alt: v })} />
            <Field label="Caption (optional)">
              <Input value={String(b.caption || '')} onChange={(e) => onUpdate({ caption: e.target.value })} />
            </Field>
            <ToggleRow label="Portrait crop (3:4)" checked={!!b.portrait} onChange={(v) => onUpdate({ portrait: v })} />
          </>
        )}

        {b.type === 'duo' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              {[0, 1].map((k) => (
                <ImageField
                  key={k}
                  compact
                  url={b.images?.[k]?.url}
                  alt={b.images?.[k]?.alt}
                  onUrl={(v) => onUpdate({ images: setImg(b.images, k, 'url', v) })}
                  onAlt={(v) => onUpdate({ images: setImg(b.images, k, 'alt', v) })}
                />
              ))}
            </div>
            <Field label="Caption (optional)">
              <Input value={String(b.caption || '')} onChange={(e) => onUpdate({ caption: e.target.value })} />
            </Field>
          </>
        )}
      </div>
    </div>
  )
}

function ImageField({ url, alt, onUrl, onAlt, compact }: { url?: string; alt?: string; onUrl: (v: string) => void; onAlt: (v: string) => void; compact?: boolean }) {
  return (
    <div className="flex gap-3">
      <div className={`shrink-0 overflow-hidden rounded-md border bg-muted ${compact ? 'h-16 w-16' : 'h-24 w-20'}`}>
        {url
          ? <img src={url} alt={alt || ''} className="h-full w-full object-cover" />
          : <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-5 w-5 text-muted-foreground" /></div>}
      </div>
      <div className="flex-1 space-y-2">
        <Input placeholder="Image URL" value={String(url || '')} onChange={(e) => onUrl(e.target.value)} />
        <Input placeholder="Alt text (for accessibility)" value={String(alt || '')} onChange={(e) => onAlt(e.target.value)} />
      </div>
    </div>
  )
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center gap-2">
      <Switch checked={checked} onCheckedChange={onChange} />
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  )
}

function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div>
      <h3 className="text-base font-semibold">{title}</h3>
      {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}
